import { Mic } from "lucide-react";
import { TResponseOrderVideos } from "@/types/projectType";
import { getPrimaryActorDetails } from "../utils/orderVideoHelpers";

type Props = {
  video: TResponseOrderVideos;
  onPreviewBackground?: () => void;
  onPreviewOutfit?: () => void;
  backgroundImage?: string;
  outfitImage?: string;
};

const ClientRequirementsCard = ({
  video,
  onPreviewBackground,
  onPreviewOutfit,
  backgroundImage,
  outfitImage,
}: Props) => {
  const actor = getPrimaryActorDetails(video.actorDetails);
  const wardrobeStyles =
    actor?.wardrobeStyle
      ?.map((w) => (typeof w === "string" ? w : w.title || ""))
      .filter(Boolean) ?? [];
  const additionalNotes = actor?.additionalNotes?.trim();

  const productPronunciations = (
    video.scriptDetails?.featuredProduct ?? []
  ).filter((p) => p.pronunciation?.live_url);

  const hasAnything =
    backgroundImage ||
    outfitImage ||
    wardrobeStyles.length > 0 ||
    additionalNotes ||
    productPronunciations.length > 0;

  if (!hasAnything) return null;

  return (
    <div className="mb-5 rounded-xl border border-[#FFD5B0] bg-[#FFF8F3] px-4 py-3">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#B54708]">
        Client requirements
      </p>

      <div className="flex flex-wrap items-start gap-3">
        {/* Selected Background pill */}
        {backgroundImage && (
          <button
            type="button"
            onClick={onPreviewBackground}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#FDBA74] bg-[#FFF4EC] px-2.5 py-1 text-[12px] font-semibold text-[#B54708] transition-colors hover:bg-[#FFEDD5]"
          >
            Selected Background · View
          </button>
        )}

        {/* Selected Outfit pill */}
        {outfitImage && (
          <button
            type="button"
            onClick={onPreviewOutfit}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#FDBA74] bg-[#FFF4EC] px-2.5 py-1 text-[12px] font-semibold text-[#B54708] transition-colors hover:bg-[#FFEDD5]"
          >
            Selected Outfit · View
          </button>
        )}

        {/* Wardrobe style chips */}
        {wardrobeStyles.map((style) => (
          <span
            key={style}
            className="inline-flex items-center rounded-full border border-[#FDBA74] bg-white px-2.5 py-1 text-[12px] font-medium text-[#344054]"
          >
            {style}
          </span>
        ))}
      </div>

      {/* Wardrobe / actor notes */}
      {additionalNotes && (
        <div className="mt-3 rounded-lg border border-[#FFD5B0] bg-white px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[#B54708]">
            Wardrobe notes
          </p>
          <p className="mt-0.5 whitespace-pre-wrap text-xs leading-5 text-[#344054]">
            {additionalNotes}
          </p>
        </div>
      )}

      {/* Product pronunciation */}
      {productPronunciations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[#B54708]">
            Product pronunciation
          </p>
          {productPronunciations.map((p, i) => (
            <div
              key={i}
              className="flex items-center gap-2 rounded-lg border border-[#FFD5B0] bg-white px-3 py-2"
            >
              <Mic className="size-3.5 shrink-0 text-[#B54708]" />
              <div className="min-w-0 flex-1">
                <p className="mb-1 truncate text-[11px] font-medium text-[#344054]">
                  {p.name}
                </p>
                <audio
                  controls
                  src={p.pronunciation.live_url as string}
                  className="h-7 w-full"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientRequirementsCard;
